/*
  EGODRIFT Partner Directory / Detail
  Version: 1.0.4

  Shopify production filename:
  assets/ego-partners.js

  Notes:
  - Loads Google Maps only when a partner section with a configured key needs it.
  - Keeps the key out of Liquid source code. The browser still receives the key, as required by Google Maps JavaScript API.
  - Marker selection keeps the page position stable and visually dims non-selected partner cards.
  - Preserves viewport position when a selected partner card is moved to the front.
  - Clicking a partner card now selects/focuses its map marker without hijacking the detail link.
*/

(function () {
  'use strict';

  var mapsPromise = null;
  var mapsKey = '';
  var mapsCallbackName = '__egoPartnerGoogleMapsReady';
  var mapsAuthFailed = false;

  function normalizeRegion(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s*\/\s*/g, '-')
      .replace(/\s+/g, '-');
  }

  function toNumber(value) {
    var number = parseFloat(value);
    return Number.isFinite(number) ? number : null;
  }

  function calculateDistance(lat1, lon1, lat2, lon2) {
    var toRad = function (value) {
      return value * Math.PI / 180;
    };
    var earthRadiusKm = 6371;
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    return earthRadiusKm * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  function showMapFallback(root, message) {
    var mapElements = root.querySelectorAll('[data-ego-partners-map], [data-ego-partners-detail-map]');
    var fallback = root.querySelector('[data-ego-partners-map-fallback]');

    mapElements.forEach(function (mapElement) {
      mapElement.classList.add('is-unavailable');
    });

    if (fallback) {
      if (message) fallback.textContent = message;
      fallback.hidden = false;
    }
  }

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) {
      return Promise.resolve(window.google.maps);
    }

    if (!apiKey) {
      return Promise.reject(new Error('Missing Google Maps API key.'));
    }

    if (mapsPromise) {
      return mapsPromise;
    }

    mapsKey = apiKey;

    mapsPromise = new Promise(function (resolve, reject) {
      window[mapsCallbackName] = function () {
        if (window.google && window.google.maps) {
          resolve(window.google.maps);
        } else {
          reject(new Error('Google Maps did not initialize.'));
        }
      };

      window.gm_authFailure = function () {
        mapsAuthFailed = true;
        reject(new Error('Google Maps authentication failed.'));
      };

      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(mapsKey) + '&callback=' + encodeURIComponent(mapsCallbackName);
      script.async = true;
      script.defer = true;
      script.onerror = function () {
        reject(new Error('Google Maps script could not be loaded.'));
      };

      document.head.appendChild(script);
    });

    return mapsPromise;
  }

  function getMarkerIcon(root) {
    var iconUrl = root.dataset.customPinUrl;

    if (!iconUrl || !(window.google && window.google.maps)) {
      return undefined;
    }

    return {
      url: iconUrl,
      scaledSize: new window.google.maps.Size(48, 24)
    };
  }

  function fitMapToMarkers(map, bounds, visibleCount) {
    if (!map || !bounds || bounds.isEmpty()) return;

    if (visibleCount === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(6);
      return;
    }

    map.fitBounds(bounds, 56);
  }

  function initDirectory(root) {
    if (!root || root.dataset.egoPartnerDirectoryInitialized === 'true') return;

    root.dataset.egoPartnerDirectoryInitialized = 'true';

    var apiKey = root.dataset.googleMapsApiKey || '';
    var nearestLimit = parseInt(root.dataset.nearestLimit || '4', 10);
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ego-partner-card]'));
    var originalCards = cards.slice();
    var list = root.querySelector('[data-ego-partners-list]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.ego-partners__region-tab'));
    var nearestButton = root.querySelector('[data-ego-partners-nearest]');
    var clearButton = root.querySelector('[data-ego-partners-clear-filter]');
    var status = root.querySelector('[data-ego-partners-filter-status]');
    var statusText = root.querySelector('[data-ego-partners-status-text]');
    var mapElement = root.querySelector('[data-ego-partners-map]');
    var map = null;
    var markers = [];
    var selectedCard = null;
    var activeMarker = null;

    function setStatus(message, mode) {
      if (statusText) statusText.textContent = message || '';

      if (status) {
        status.hidden = !message;
        status.dataset.statusMode = message ? (mode || '') : '';
      }
    }

    function setActiveRegion(regionValue) {
      var selectedRegion = normalizeRegion(regionValue);

      tabs.forEach(function (tab) {
        var isActive = normalizeRegion(tab.dataset.region) === selectedRegion;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (!selectedRegion) {
        var allRegionsTab = root.querySelector('.ego-partners__region-tab[data-region=""]');
        if (allRegionsTab) {
          allRegionsTab.classList.add('is-active');
          allRegionsTab.setAttribute('aria-selected', 'true');
        }
      }
    }

    function getVisibleCards() {
      return originalCards.filter(function (card) {
        return !card.hidden;
      });
    }

    function updateMarkerStyles() {
      markers.forEach(function (record) {
        if (!record || !record.marker) return;

        var isSelected = selectedCard && record.card === selectedCard;
        record.marker.setZIndex(isSelected ? 1000 : null);
      });
    }

    function updateSelectionStyles() {
      var hasSelection = Boolean(selectedCard);

      root.classList.toggle('is-partner-selected', hasSelection);

      if (list) {
        list.classList.toggle('is-selection-active', hasSelection);
      }

      originalCards.forEach(function (card) {
        var isSelected = card === selectedCard;
        var isVisible = !card.hidden;

        card.classList.toggle('is-selected', hasSelection && isSelected);
        card.classList.toggle('is-highlighted', hasSelection && isSelected);
        card.classList.toggle('is-dimmed', hasSelection && isVisible && !isSelected);
      });

      updateMarkerStyles();
    }

    function clearSelectedPartner() {
      selectedCard = null;
      updateSelectionStyles();
    }

    function renderCards(nextCards) {
      if (!list) return;

      var visibleSet = new Set(nextCards);
      list.innerHTML = '';

      originalCards.forEach(function (card) {
        card.hidden = !visibleSet.has(card);
      });

      nextCards.forEach(function (card) {
        card.hidden = false;
        list.appendChild(card);
      });

      updateSelectionStyles();
    }

    function updateUrl(regionValue) {
      var selectedRegion = normalizeRegion(regionValue);
      var url = new URL(window.location.href);

      if (selectedRegion) {
        url.searchParams.set('region', selectedRegion);
      } else {
        url.searchParams.delete('region');
      }

      window.history.replaceState({}, '', url);
    }

    function clearMarkers() {
      markers.forEach(function (record) {
        if (record && record.marker) record.marker.setMap(null);
      });
      markers = [];
      activeMarker = null;
    }

    function getCardTitle(card) {
      if (!card) return 'EGODRIFT Partner';

      var nameElement = card.querySelector('.ego-partners__name');
      return nameElement ? nameElement.textContent.trim() : 'EGODRIFT Partner';
    }

    function getMarkerRecordForCard(card) {
      for (var index = 0; index < markers.length; index += 1) {
        if (markers[index].card === card) return markers[index];
      }

      return null;
    }

    function focusMapOnPartner(card) {
      if (!map || !(window.google && window.google.maps) || !card) return;

      var record = getMarkerRecordForCard(card);
      var lat = toNumber(card.dataset.lat);
      var lng = toNumber(card.dataset.lng);

      if (lat === null || lng === null) return;

      map.panTo({ lat: lat, lng: lng });

      if (map.getZoom && map.getZoom() < 9) {
        map.setZoom(9);
      }

      if (record && record.marker) {
        activeMarker = record.marker;
        record.marker.setZIndex(1000);

        if (window.google.maps.Animation) {
          record.marker.setAnimation(window.google.maps.Animation.BOUNCE);

          window.setTimeout(function () {
            if (record.marker && activeMarker === record.marker) {
              record.marker.setAnimation(null);
            }
          }, 700);
        }
      }
    }

    function selectPartner(card, title, options) {
      if (!card || !list) return;

      var config = options || {};
      var previousScrollX = window.scrollX;
      var previousScrollY = window.scrollY;

      selectedCard = card;

      if (config.moveCardToFront) {
        list.prepend(card);
      }

      updateSelectionStyles();

      if (config.focusMap) {
        focusMapOnPartner(card);
      }

      setStatus('Selected partner: ' + (title || getCardTitle(card)) + '.', 'selection');

      if (config.preserveScroll) {
        window.requestAnimationFrame(function () {
          window.scrollTo(previousScrollX, previousScrollY);
        });
      }
    }

    function selectPartnerFromMarker(card, title) {
      selectPartner(card, title, {
        moveCardToFront: true,
        focusMap: false,
        preserveScroll: true
      });
    }

    function selectPartnerFromCard(card) {
      selectPartner(card, getCardTitle(card), {
        moveCardToFront: false,
        focusMap: true,
        preserveScroll: true
      });
    }

    function updateMapMarkers() {
      if (!map || !(window.google && window.google.maps)) return;

      clearMarkers();

      var bounds = new window.google.maps.LatLngBounds();
      var visibleCards = getVisibleCards();
      var icon = getMarkerIcon(root);

      visibleCards.forEach(function (card) {
        var lat = toNumber(card.dataset.lat);
        var lng = toNumber(card.dataset.lng);
        var nameElement = card.querySelector('.ego-partners__name');
        var title = nameElement ? nameElement.textContent.trim() : 'EGODRIFT Partner';

        if (lat === null || lng === null) return;

        var marker = new window.google.maps.Marker({
          position: { lat: lat, lng: lng },
          map: map,
          title: title,
          icon: icon
        });

        marker.addListener('click', function () {
          selectPartnerFromMarker(card, title);
        });

        markers.push({
          marker: marker,
          card: card,
          title: title
        });
        bounds.extend({ lat: lat, lng: lng });
      });

      fitMapToMarkers(map, bounds, visibleCards.length);
      updateMarkerStyles();
    }

    function applyRegionFilter(regionValue, shouldUpdateUrl) {
      var selectedRegion = normalizeRegion(regionValue);
      var filteredCards = originalCards.filter(function (card) {
        var cardRegion = normalizeRegion(card.dataset.region);
        return selectedRegion === '' || cardRegion === selectedRegion;
      });

      clearSelectedPartner();
      setActiveRegion(selectedRegion);
      renderCards(filteredCards);
      setStatus('', '');

      if (shouldUpdateUrl) updateUrl(selectedRegion);
      updateMapMarkers();
    }

    function showNearestDealers() {
      if (!navigator.geolocation) {
        setStatus('Location access is not available in this browser.', 'notice');
        return;
      }

      navigator.geolocation.getCurrentPosition(function (position) {
        var userLat = position.coords.latitude;
        var userLng = position.coords.longitude;
        var sortedCards = originalCards
          .map(function (card) {
            var lat = toNumber(card.dataset.lat);
            var lng = toNumber(card.dataset.lng);

            return {
              card: card,
              distance: lat !== null && lng !== null ? calculateDistance(userLat, userLng, lat, lng) : Infinity
            };
          })
          .sort(function (a, b) {
            return a.distance - b.distance;
          })
          .slice(0, Math.max(nearestLimit, 1))
          .map(function (item) {
            return item.card;
          });

        clearSelectedPartner();
        setActiveRegion('');
        renderCards(sortedCards);
        setStatus('Showing the partners closest to you.', 'nearest');
        updateUrl('');
        updateMapMarkers();
      }, function () {
        setStatus('Location access was denied or is currently unavailable.', 'notice');
      });
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function (event) {
        var interactiveElement = event.target.closest('a, button, input, select, textarea, summary');

        if (interactiveElement) return;

        selectPartnerFromCard(card);
      });
    });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        applyRegionFilter(tab.dataset.region || '', true);
      });
    });

    if (nearestButton) {
      nearestButton.addEventListener('click', showNearestDealers);
    }

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        if (status && status.dataset.statusMode === 'selection') {
          clearSelectedPartner();
          setStatus('', '');
          return;
        }

        applyRegionFilter('', true);
      });
    }

    window.addEventListener('popstate', function () {
      var urlParams = new URLSearchParams(window.location.search);
      applyRegionFilter(urlParams.get('region') || '', false);
    });

    var urlParams = new URLSearchParams(window.location.search);
    applyRegionFilter(urlParams.get('region') || '', false);

    if (mapElement) {
      loadGoogleMaps(apiKey).then(function () {
        if (mapsAuthFailed) {
          showMapFallback(root, 'The partner map is currently unavailable. Please use the partner list below.');
          return;
        }

        map = new window.google.maps.Map(mapElement, {
          zoom: 2,
          center: { lat: 20, lng: 0 },
          mapId: 'EGO_PARTNER_DIRECTORY_MAP'
        });

        updateMapMarkers();
      }).catch(function () {
        showMapFallback(root, 'The partner map is currently unavailable. Please use the partner list below.');
      });
    }
  }

  function initDetail(root) {
    if (!root || root.dataset.egoPartnerDetailInitialized === 'true') return;

    root.dataset.egoPartnerDetailInitialized = 'true';

    var apiKey = root.dataset.googleMapsApiKey || '';
    var mapElement = root.querySelector('[data-ego-partners-detail-map]');
    var lat = toNumber(root.dataset.lat);
    var lng = toNumber(root.dataset.lng);
    var title = root.dataset.partnerName || 'EGODRIFT Partner';

    if (!mapElement || lat === null || lng === null) return;

    loadGoogleMaps(apiKey).then(function () {
      if (mapsAuthFailed) {
        showMapFallback(root, 'The partner map is currently unavailable.');
        return;
      }

      var map = new window.google.maps.Map(mapElement, {
        zoom: 14,
        center: { lat: lat, lng: lng },
        mapId: 'EGO_PARTNER_DETAIL_MAP'
      });

      new window.google.maps.Marker({
        position: { lat: lat, lng: lng },
        map: map,
        title: title,
        icon: getMarkerIcon(root)
      });
    }).catch(function () {
      showMapFallback(root, 'The partner map is currently unavailable.');
    });
  }

  function init() {
    document.querySelectorAll('[data-ego-partner-directory]').forEach(initDirectory);
    document.querySelectorAll('[data-ego-partner-detail]').forEach(initDetail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
